import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { safeSessionStorage } from '@/shared/lib';
import { toastCaller } from '@/shared/ui';

import { composedModel } from '@/entities/composed';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { patientModel } from '@/entities/patient';
import { transcriptorModel } from '@/entities/transcriptor';

import {
  HANDLED_PRESIGN_SIGNATURE_STORAGE_KEY,
  OPEN_TRANSCRIPTOR_RECORDING_PARAM,
} from '../config';
import { getPresignDeepLinkSignature } from '../lib';

export const useAutoOpenTranscriptorRecording = (): void => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const { patientID = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasHandledDeepLink = useRef(false);

  const hasOpenRecordingParam =
    searchParams.get(OPEN_TRANSCRIPTOR_RECORDING_PARAM) === '1';

  // The partner quick start guide presigns the patient page URL itself, so a
  // basic presign deep link carries no extra trigger param. The handled
  // signature is remembered per tab to keep refresh/back from re-opening the
  // modal, mirroring how the explicit param is stripped after handling.
  const presignSignature = getPresignDeepLinkSignature(searchParams);
  const isPresignDeepLinkHandled =
    presignSignature !== null &&
    safeSessionStorage.getItem(HANDLED_PRESIGN_SIGNATURE_STORAGE_KEY) ===
      presignSignature;

  const shouldOpen =
    hasOpenRecordingParam ||
    (presignSignature !== null && !isPresignDeepLinkHandled);

  const currentPatientID = useAppSelector(
    patientModel.selectors.selectCurrentPatientID,
  );
  const organizationLoading = useAppSelector(
    organizationModel.selectors.selectLoading,
  );
  const isOrganizationSettingsLoaded = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationSettingsLoaded,
  );
  const { canOrder: canOrderTranscriptor, isReady: isTranscriptorReady } =
    useAppSelector(composedModel.selectors.selectTranscriptorAvailability);
  const isRecording = useAppSelector(
    transcriptorModel.selectors.selectIsRecording,
  );

  // The patient profile stream switches the current organization before it
  // sets the current patient, so a matching current patient guarantees the
  // organization switch has already been dispatched.
  const isReady =
    Boolean(patientID) &&
    currentPatientID === patientID &&
    organizationLoading === 'succeeded' &&
    isOrganizationSettingsLoaded &&
    isTranscriptorReady;

  useEffect(() => {
    if (!shouldOpen || !isReady || hasHandledDeepLink.current) {
      return;
    }

    hasHandledDeepLink.current = true;

    if (canOrderTranscriptor && !isRecording) {
      dispatch(
        modalModel.actions.openModal({
          modalID: ModalID.TranscriptorRecording,
          data: { patientID },
        }),
      );
    } else {
      toastCaller({
        type: 'error',
        message: isRecording
          ? formatMessage({
              id: 'transcriptorDeepLink.recordingInProgress',
              defaultMessage: 'Recording is already in progress',
            })
          : formatMessage({
              id: 'transcriptorDeepLink.unavailable',
              defaultMessage:
                'Transcriptor is not available for this organization',
            }),
        autoClose: 10000,
      });
    }

    if (presignSignature !== null) {
      safeSessionStorage.setItem(
        HANDLED_PRESIGN_SIGNATURE_STORAGE_KEY,
        presignSignature,
      );
    }

    if (hasOpenRecordingParam) {
      // Only our param is stripped; X-Dc-* params stay for presign bootstrap
      setSearchParams(
        (params) => {
          params.delete(OPEN_TRANSCRIPTOR_RECORDING_PARAM);
          return params;
        },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldOpen, isReady, canOrderTranscriptor, isRecording, patientID]);
};
